import _cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import { ITaskInformation } from '@Data/processes/Interfaces';
import { processEndPoints } from '@Services/data/ApiHandler';
import { error, success } from '@Services/notification/NotificationServices';
import store from '@Services/redux/Store';
import FoldersApi from '../folders/Api';
import {
    getProgressStateFromString,
    getRawProcessName,
    getRemainingTimeString,
} from './Helpers';
import {
    INotification,
    NotificationSeverity,
    NotificationType,
    ProgressState,
} from './Interfaces';
import { NotificationActions } from './Reducer';

const Api = {
    addNotification: async (
        message: string,
        severity: NotificationSeverity,
        type: NotificationType,
        id: string,
        additonalInfo?: any
    ) => {
        const notification: INotification = {
            message: message,
            severity: severity,
            type: type,
            id: id,
            created: Date.now().toString(),
        };

        if (type === NotificationType.PROGRESS) {
            notification.progressInfo = {
                state: ProgressState.PENDING,
                progressPercentage: 0,
                remainingTime: null,
            };
        }

        if (type === NotificationType.SEQUENTIAL) {
            notification.sequentialInfo = {
                remaining: additonalInfo.total,
                total: additonalInfo.total,
            };
        }

        store.dispatch(NotificationActions.addNotification(notification));
        store.dispatch(NotificationActions.setHasUnreadNotifications());

        if (type === NotificationType.PROGRESS) {
            await Api.updateRunningProcessInformation(notification, () => {
                const selectedProject =
                    store.getState().project.selectedProject;
                if (selectedProject) {
                    FoldersApi.getFolders(
                        selectedProject.project_id,
                        null,
                        true
                    );
                }
            });
        }
    },
    removeNotification: (id: string, callback?: () => void) => {
        store.dispatch(NotificationActions.removeNotification(id));
        if (callback) {
            callback();
        }
    },
    updateRunningProcessInformation: async (
        runningProcessNotification: INotification,
        refreshFoldersCallback?: () => void
    ) => {
        const response = await processEndPoints.getProcessProgress(
            runningProcessNotification.id
        );
        if (response.isSuccess) {
            const info: ITaskInformation = response.data;

            const updatedNotification: INotification = _cloneDeep(
                runningProcessNotification
            );
            updatedNotification.progressInfo.state = getProgressStateFromString(
                info.state
            );

            switch (updatedNotification.progressInfo.state) {
                case ProgressState.FAILURE:
                    updatedNotification.severity = NotificationSeverity.ERROR;
                    error(
                        `${getRawProcessName(
                            updatedNotification.message
                        )} failed.`
                    );
                    break;
                case ProgressState.SUCCESS:
                    updatedNotification.severity = NotificationSeverity.SUCCESS;
                    success(
                        `${getRawProcessName(
                            updatedNotification.message
                        )} was successful!`
                    );
                    break;
                case ProgressState.PENDING:
                    updatedNotification.severity = NotificationSeverity.INFO;
                    updatedNotification.progressInfo.progressPercentage =
                        (info.progress.current / info.progress.total) * 100;
                    updatedNotification.progressInfo.remainingTime =
                        getRemainingTimeString(info.progress);
                    break;
            }

            store.dispatch(
                NotificationActions.updateNotification(updatedNotification)
            );

            if (
                updatedNotification.progressInfo.state === ProgressState.PENDING
            ) {
                setTimeout(() => {
                    return Api.updateRunningProcessInformation(
                        runningProcessNotification,
                        refreshFoldersCallback
                    );
                }, 1500);
            } else if (
                updatedNotification.progressInfo.state === ProgressState.SUCCESS
            ) {
                if (refreshFoldersCallback) {
                    refreshFoldersCallback();
                }
            }
        } else {
            const updatedNotification: INotification = _cloneDeep(
                runningProcessNotification
            );
            updatedNotification.severity = NotificationSeverity.ERROR;
            error(`${getRawProcessName(updatedNotification.message)} failed.`);
            store.dispatch(
                NotificationActions.updateNotification(updatedNotification)
            );
        }
    },
    setAllNotificationsRead: () => {
        store.dispatch(NotificationActions.setAllNotificationsRead());
    },
    cancelProgressNotification: (notification: INotification) => {
        const updatedNotification: INotification = _cloneDeep(notification);
        updatedNotification.progressInfo.state = ProgressState.CANCELLED;
        store.dispatch(
            NotificationActions.updateNotification(updatedNotification)
        );
    },
    updateSequentialNotification: (id: string, remaining: number) => {
        const notification = store
            .getState()
            .notification.notifications.find((n) => n.id === id);
        if (!_isNil(notification)) {
            const updatedNotification: INotification = _cloneDeep(notification);
            updatedNotification.sequentialInfo.remaining = remaining;

            if (remaining === 0) {
                updatedNotification.severity = NotificationSeverity.SUCCESS;
            }

            store.dispatch(
                NotificationActions.updateNotification(updatedNotification)
            );
        }
    },
    removeProcessNotifications: (callback?: () => void) => {
        const progressNotifications = store
            .getState()
            .notification.notifications.filter(
                (n) => n.type === NotificationType.PROGRESS
            );

        progressNotifications.forEach((n) => Api.cancelProgressNotification(n));

        store.dispatch(NotificationActions.removeProcessNotifications());

        if (callback) {
            callback();
        }
    },
};

export default Api;
